
import Vue from "vue";
import { Component, Prop, Inject } from "vue-property-decorator";
import { shouldShowControl } from "@/lib/controls/should-show-control";
import Control_ from "./Control.vue";
import { ControlsTreeContext, Control } from "@/types/controls";
import Accordion from "@/components/UI/Accordion.vue";
import Popout from "@/components/UI/Popout.vue";
import { clear, valuePresent } from "@/lib/controls/values";
import { configStore, uiStore } from "@/store/store-accessor";
import IndicatorLight from "./IndicatorLight.vue";
import Modal from "@/components/UI/Modal.vue";
import ControlsTreeControlSectionContextMenu from "@/components/Reusable/ControlsTree/ControlsTreeControlSectionContextMenu.vue";
import OxyIndicatorLights from "@/oxygen/classes/OxyIndicatorLights.vue";
import OxygenControlName from "@/oxygen/classes/OxygenControlName.vue";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";

@Component({
  name: "ControlsTreeExpansionPanels",
  components: {
    OxygenControlName,
    OxyIndicatorLights,
    Modal,
    Control: Control_,
    Accordion,
    Popout,
    IndicatorLight,
    ControlsTreeControlSectionContextMenu
  }
})
export default class ControlsTreeExpansionPanels extends Vue {
  @Prop()
  controls!: Control[];

  @Prop({ default: false })
  allowPopoutsInRoot!: boolean;

  get builderMode() {
    return configStore.builderMode;
  }

  get thereIsOnlyOneSection() {
    return (
      this.controls.length === 1 && this.controls[0].options.type === "section"
    );
  }

  isPopoutOrModal(control: Control) {
    const type = control.options.sectionOptions?.type;
    const isNested = this.path.includes(".");

    if (!type) return false;

    return (
      ["popout", "modal"].includes(type) &&
      (this.allowPopoutsInRoot || isNested)
    );
  }

  @Prop({
    default: false
  })
  forceFirstSectionOpen!: boolean;

  @Prop({
    default: false
  })
  compact!: boolean;

  @Prop({
    default: false
  })
  nested!: boolean;

  @Prop({ default: 0 })
  depth!: number;

  @Prop()
  path!: string;

  isAccordionAtPathExpanded(
    controlPathWithSlug: string,
    index: number,
    type?: "popout" | "accordion"
  ) {
    const isExpanded = this.context.getAccordionState(controlPathWithSlug);

    if (isExpanded !== undefined) {
      return isExpanded;
    }

    if (
      type !== "popout" &&
      (this.forceFirstSectionOpen || this.thereIsOnlyOneSection) &&
      // only open the first one
      index === 0
    ) {
      // toggle it to "true" since it's open but not in the state yet
      this.toggleAccordion(controlPathWithSlug);
      return true;
    }

    return false;
  }

  toggleAccordion(controlPathWithSlug: string) {
    const currentState = this.context.getAccordionState(controlPathWithSlug);
    this.context.setAccordionState(controlPathWithSlug, !currentState);
  }

  setAccordionState(controlPathWithSlug: string, state: boolean) {
    this.context.setAccordionState(controlPathWithSlug, state);
  }

  @Inject("context") readonly context!: ControlsTreeContext;

  classForConditionalDisplayOfControl(control: Control, path: string) {

    if (control.options.disabledInFutureLayer && isFutureLayerMode()) {
      return 'conditional-control-display-hidden';
    }
    
    if (control.options.condition || control.options.hideForElements) {
      if (
        !shouldShowControl(
          control,
          path,
          this.context,
          uiStore.currentBreakpoint.id
        )
      ) {
        return "conditional-control-display-hidden";
      }
    }

    return "conditional-control-display-visible";
  }

  get stylesPresent() {
    return (path: string, control: Control) =>
      valuePresent(path, control, this.context);
  }

  clearStyles(path: string, control: Control) {
    clear(path, control, this.context);
  }
}
